/**
 * Stroke power and shooting-mode math (authentic).
 *
 * Power scales with drag distance; the four shooting modes rotate/flip the
 * resulting velocity. The launch velocity is exactly the mode-rotated power
 * vector — the original's hidden constant velocity offset was dropped
 * deliberately so shots fly along the drawn aim line.
 */
import { ShootingMode } from '../types.js';
import { POWER_DEAD_ZONE, POWER_DIVISOR, MIN_POWER, MAX_POWER, STROKE_NOISE } from '../constants.js';
import { Rng } from './rng.js';

export interface Vel {
  vx: number;
  vy: number;
}

/** Drag vector -> power vector (normalized direction * clamped scale). */
export function strokePower(dx: number, dy: number): Vel {
  const distance = Math.sqrt(dx * dx + dy * dy);
  if (distance === 0) return { vx: 0, vy: 0 };
  let scale = (distance - POWER_DEAD_ZONE) / POWER_DIVISOR;
  if (scale < MIN_POWER) scale = MIN_POWER;
  if (scale > MAX_POWER) scale = MAX_POWER;
  const k = scale / distance;
  return { vx: dx * k, vy: dy * k };
}

function applyMode(p: Vel, mode: ShootingMode): Vel {
  switch (mode) {
    case ShootingMode.Reverse:
      return { vx: -p.vx, vy: -p.vy };
    case ShootingMode.Right:
      return { vx: p.vy, vy: -p.vx };
    case ShootingMode.Left:
      return { vx: -p.vy, vy: p.vx };
    case ShootingMode.Normal:
    default:
      return p;
  }
}

/**
 * The mode-rotated power vector with no noise — what the aim line draws.
 * The noiseless launch velocity is identical, so the ball flies along the
 * drawn line (in Direct mode it points straight at the cursor).
 */
export function strokeAim(dx: number, dy: number, mode: ShootingMode): Vel {
  return applyMode(strokePower(dx, dy), mode);
}

/**
 * Drag vector + mode -> initial ball velocity (the mode-rotated power vector).
 *
 * When `seed` is provided, the original's tiny per-shot random perturbation is
 * applied on top (seeded, so the predicting client and the authoritative
 * server produce the identical velocity). Omit `seed` for the noiseless
 * vector — identical to `strokeAim` — used by the golden tests.
 */
export function strokeVelocity(dx: number, dy: number, mode: ShootingMode, seed?: number): Vel {
  const speed = applyMode(strokePower(dx, dy), mode);
  let vx = speed.vx;
  let vy = speed.vy;
  if (seed !== undefined) {
    // Decorrelate from the in-sim RNG stream (mines) which uses the raw seed.
    const rng = new Rng((seed ^ 0x6a09e667) >>> 0);
    vx += rng.float(-STROKE_NOISE, STROKE_NOISE);
    vy += rng.float(-STROKE_NOISE, STROKE_NOISE);
  }
  return { vx, vy };
}
